/**
 * Where the chat token lives, shared by the HTTP client, SignalR and the chat page.
 * localStorage rather than a cookie: in the website widget the chat runs in an iframe on the hotel's site, where
 * Safari and Firefox drop third-party cookies, while iframe storage is kept per embedding site. The token only ever
 * travels as a Bearer header, so the server never needed a cookie.
 */
type TokenListener = () => void;

const STORAGE_KEY = 'xenioai.chat.token';

const listeners = new Set<TokenListener>();
/** Set when the browser refuses storage (private mode, blocked site data): the token then lives for this page only. */
let memoryOnly = false;
let memoryToken: string | undefined;

const notify = () => listeners.forEach((listener) => listener());

export const getChatToken = (): string | undefined => {
   if (memoryOnly) return memoryToken;
   try {
      return window.localStorage.getItem(STORAGE_KEY) ?? undefined;
   } catch {
      return memoryToken;
   }
};

export const setChatToken = (token: string | undefined): void => {
   memoryToken = token || undefined;
   try {
      if (memoryToken) window.localStorage.setItem(STORAGE_KEY, memoryToken);
      else window.localStorage.removeItem(STORAGE_KEY);
   } catch {
      memoryOnly = true;
   }
   notify();
};

export const clearChatToken = (): void => setChatToken(undefined);

export const subscribeChatToken = (listener: TokenListener): (() => void) => {
   listeners.add(listener);
   return () => {
      listeners.delete(listener);
   };
};

// Another tab signed in or out: follow it.
window.addEventListener('storage', (event) => {
   if (event.key === STORAGE_KEY || event.key === null) notify();
});
